/**
 * Inline or near-fullscreen chrome around the protocol DAG editor.
 */

import { Maximize2, Minimize2 } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect } from "react";

type ProtocolDagShellProps = {
  isExpanded: boolean;
  onToggleExpanded: () => void;
  summary: ReactNode;
  children: ReactNode;
};

/**
 * Wrap the DAG canvas in page or almost-fullscreen layout.
 *
 * @param props - Expand state, summary strip, and canvas content
 * @returns Shell element
 */
export function ProtocolDagShell({
  isExpanded,
  onToggleExpanded,
  summary,
  children,
}: ProtocolDagShellProps) {
  useEffect(() => {
    if (!isExpanded) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onToggleExpanded();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isExpanded, onToggleExpanded]);

  const shellClass = isExpanded
    ? "fixed inset-2 z-[100] flex h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] flex-col rounded-xl border border-slate-300 bg-white p-4 shadow-2xl"
    : "rounded-lg border border-slate-200 bg-white p-4";

  return (
    <>
      {isExpanded ? (
        <div className="fixed inset-0 z-[90] bg-slate-900/40" aria-hidden />
      ) : null}
      <div className={shellClass} role={isExpanded ? "dialog" : undefined}>
        <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
            {summary}
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-800 hover:border-slate-900"
            onClick={onToggleExpanded}
            aria-label={isExpanded ? "Exit expanded graph" : "Expand graph"}
          >
            {isExpanded ? (
              <>
                <Minimize2 className="h-3.5 w-3.5" />
                Exit expand
              </>
            ) : (
              <>
                <Maximize2 className="h-3.5 w-3.5" />
                Expand
              </>
            )}
          </button>
        </div>
        <div
          className={
            isExpanded
              ? "flex min-h-0 flex-1 flex-col overflow-hidden"
              : "contents"
          }
        >
          {children}
        </div>
      </div>
    </>
  );
}
